import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, 
  Clock, 
  CheckCircle, 
  Phone, 
  MessageCircle,
  ChevronRight,
  AlertTriangle,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const FollowUps = ({ user }) => {
  const navigate = useNavigate();
  const [followups, setFollowups] = useState({ overdue: [], today: [], upcoming: [] });
  const [loading, setLoading] = useState(true);

  const fetchFollowups = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/followups/grouped`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFollowups(data);
      }
    } catch (error) {
      toast.error('Failed to load follow-ups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFollowups();
  }, [fetchFollowups]);

  const handleMarkDone = async (followupId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/followups/${followupId}/done`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Follow-up marked as done');
        fetchFollowups();
      } else {
        toast.error('Failed to update follow-up');
      }
    } catch (error) {
      toast.error('Failed to update follow-up');
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const FollowUpCard = ({ followup, isOverdue }) => (
    <Card 
      className={`p-4 bg-white border-0 shadow-sm rounded-xl ${isOverdue ? 'border-l-4 border-l-red-500' : ''}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0" onClick={() => navigate(`/crm/${followup.lead_id}`)}>
          <h3 className="font-semibold text-slate-800 truncate">{followup.customer_name}</h3>
          <p className="text-sm text-slate-500">{followup.phone}</p>
          {followup.notes && (
            <p className="text-xs text-slate-400 mt-1 line-clamp-2">{followup.notes}</p>
          )}
          <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
            <Clock size={12} />
            <span>{formatTime(followup.due_at)}</span>
            {followup.channel && (
              <span className="px-2 py-0.5 bg-slate-100 rounded">{followup.channel}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-8 px-3"
            onClick={() => handleMarkDone(followup.followup_id)}
          >
            <CheckCircle size={14} className="mr-1" />
            Done
          </Button>
          <div className="flex gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={() => window.open(`tel:${followup.phone}`, '_self')}
            >
              <Phone size={14} />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={() => window.open(`https://wa.me/91${followup.phone?.replace(/\D/g, '')}`, '_blank')}
            >
              <MessageCircle size={14} />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );

  const Section = ({ title, items, icon: Icon, color, isOverdue }) => {
    if (!items || items.length === 0) return null;
    
    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Icon size={18} className={color} />
          <h2 className="font-semibold text-slate-700">{title}</h2>
          <span className="ml-auto text-sm text-slate-500">{items.length}</span>
        </div>
        <div className="space-y-2">
          {items.map((followup) => (
            <FollowUpCard key={followup.followup_id} followup={followup} isOverdue={isOverdue} />
          ))}
        </div>
      </div>
    );
  };

  const totalCount = (followups.overdue?.length || 0) + (followups.today?.length || 0) + (followups.upcoming?.length || 0);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header - Standardized ← Title format */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-4 py-4 flex items-center gap-2">
          <button 
            onClick={() => navigate(-1)} 
            className="p-1.5 -ml-1.5 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="page-back-btn"
          >
            <ArrowLeft size={20} className="text-slate-700" strokeWidth={2.5} />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-slate-800">Follow-ups</h1>
            <p className="text-xs text-slate-500">{totalCount} pending</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EF4444] mx-auto mb-3"></div>
            <p className="text-sm text-slate-500">Loading follow-ups...</p>
          </div>
        ) : totalCount === 0 ? (
          <Card className="p-8 text-center bg-white border-0 shadow-sm rounded-xl">
            <CheckCircle size={40} className="mx-auto mb-3 text-emerald-400" />
            <p className="text-slate-600 font-medium">All caught up!</p>
            <p className="text-sm text-slate-400">No pending follow-ups</p>
          </Card>
        ) : (
          <>
            <Section 
              title="Overdue" 
              items={followups.overdue} 
              icon={AlertTriangle} 
              color="text-[#5B3DF5]"
              isOverdue={true}
            />
            <Section 
              title="Today" 
              items={followups.today} 
              icon={Clock} 
              color="text-amber-500"
            />
            <Section 
              title="Upcoming" 
              items={followups.upcoming} 
              icon={Calendar} 
              color="text-blue-500"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default FollowUps;
